<template>
    <v-dialog v-model="show" @click:outside="close" width="680px">
        <div class="modal cc-resend-modal">
            <div class="cc-resend-modal__icon">
                <img :src="iconSrc" alt=""/>
            </div>
            <div class="cc-resend-modal__body">
                <h6 class="modal-title mb-3">{{ titleText }}</h6>
                <p class="cc-p" style="font-family: Roboto !important; font-weight: 400 !important; margin-bottom: 12px;">{{ summaryText }}</p>
                <ul v-if="warnings.length" class="cc-warnings" style="font-family: Roboto !important; font-size: 13px; color: #B45309; padding-left: 18px; margin-bottom: 16px;">
                    <li v-for="(w, i) in warnings" :key="i" style="margin-bottom: 4px;">{{ w }}</li>
                </ul>
                <div class="cc-resend-modal__actions">
                    <v-btn class="btn-blanco c-white-resend" rounded depressed @click="close">Ok</v-btn>
                </div>
            </div>
        </div>
    </v-dialog>
</template>
<script>
export default {
    props: {
        show: Boolean,
        hide: Function,
        // Array of { user, email_status, email_skip_reason, sms_status, sms_skip_reason }
        results: { type: Array, default: () => [] },
    },
    computed: {
        // Aggregate every per-user result into one summary so the modal can
        // describe the outcome accurately, even if multiple users were
        // selected. Cleaner than showing one row per user in this small UI.
        agg() {
            const a = { email_sent: 0, email_skipped: 0, email_failed: 0, sms_sent: 0, sms_skipped: 0, sms_failed: 0, email_unsubscribed: [], sms_unsubscribed: [] };
            (this.results || []).forEach(r => {
                if (r.email_status === 'sent') a.email_sent++;
                else if (r.email_status === 'skipped') {
                    a.email_skipped++;
                    if (r.email_skip_reason === 'unsubscribed') a.email_unsubscribed.push((r.user && (r.user.email || r.user.name)) || 'a recipient');
                } else a.email_failed++;
                if (r.sms_status === 'sent') a.sms_sent++;
                else if (r.sms_status === 'skipped') {
                    a.sms_skipped++;
                    if (r.sms_skip_reason === 'unsubscribed') a.sms_unsubscribed.push((r.user && (r.user.phone || r.user.name)) || 'a recipient');
                } else a.sms_failed++;
            });
            return a;
        },
        anyDelivered() {
            return this.agg.email_sent > 0 || this.agg.sms_sent > 0;
        },
        allBlocked() {
            const r = this.results || [];
            return r.length > 0 && r.every(x => x.email_status !== 'sent' && x.sms_status !== 'sent');
        },
        iconSrc() {
            return this.allBlocked
                ? require('@/assets/img/warning.svg')
                : require('@/assets/img/warning.svg');
        },
        titleText() {
            if (this.allBlocked) return 'Invite not delivered';
            if (this.agg.email_skipped || this.agg.sms_skipped || this.agg.email_failed || this.agg.sms_failed) {
                return 'Invite partially delivered';
            }
            return 'Resend Invite';
        },
        summaryText() {
            const results = this.results || [];
            if (results.length === 0) {
                return 'Your invite has been successfully resent via text and email.';
            }
            if (this.allBlocked) {
                return 'We could not deliver the invite. Both channels were blocked or failed — see details below and try another way to reach this user.';
            }
            const parts = [];
            if (this.agg.email_sent) parts.push('email');
            if (this.agg.sms_sent) parts.push('text');
            if (parts.length === 0) return 'Invite was not delivered.';
            // Multi-recipient: include count of recipients reached per channel so
            // admins know whether a bulk action partially succeeded.
            if (results.length > 1) {
                const labels = [];
                if (this.agg.email_sent) labels.push(`email (${this.agg.email_sent} of ${results.length})`);
                if (this.agg.sms_sent) labels.push(`text (${this.agg.sms_sent} of ${results.length})`);
                return `Invite resent via ${labels.join(' and ')}.`;
            }
            return `Invite resent via ${parts.join(' and ')}.`;
        },
        warnings() {
            const out = [];
            if (this.agg.email_unsubscribed.length) {
                const who = this.agg.email_unsubscribed.join(', ');
                out.push(`Email skipped — ${who} is on the do-not-email list.`);
            }
            if (this.agg.sms_unsubscribed.length) {
                const who = this.agg.sms_unsubscribed.join(', ');
                out.push(`Text skipped — ${who} has unsubscribed from SMS (texted STOP).`);
            }
            const otherEmailFails = this.agg.email_failed;
            if (otherEmailFails) out.push(`Email delivery failed for ${otherEmailFails} recipient(s).`);
            const otherSmsFails = this.agg.sms_failed;
            if (otherSmsFails) out.push(`Text delivery failed for ${otherSmsFails} recipient(s).`);
            return out;
        },
    },
    methods: {
        close() { this.hide(); },
    },
};
</script>

<style scoped>
.cc-resend-modal {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    min-height: 240px;
}
.cc-resend-modal__icon {
    flex: 0 0 220px;
    background: #2bace2;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.cc-resend-modal__icon img {
    max-width: 100%;
    height: auto;
}
.cc-resend-modal__body {
    flex: 1 1 auto;
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 0;
}
.cc-resend-modal__actions {
    margin-top: 8px;
    display: flex;
    justify-content: flex-start;
}
</style>
